import type {
  Lfa_Conversion_Location_Type,
  Lfa_Service_Details,
  LfaServiceDetailsFieldsFragment,
} from '~/graphql/types';

export type UpdateSettingsAction =
  | { type: 'SET_DESCRIPTION'; payload: string }
  | {
      type: 'TOGGLE_SERVICE';
      payload: { service: Lfa_Service_Details };
    }
  | {
      type: 'TOGGLE_LOCATIONS';
      payload: {
        serviceId: string;
        location: Lfa_Conversion_Location_Type;
      };
    }
  | {
      type: 'SET_LOCATION_NOTE_AND_WEIGHT';
      payload: {
        serviceId: string;
        locationType: Lfa_Conversion_Location_Type;
        note: string;
        weight: number;
      };
    }
  | { type: 'SET_MIN_LEADSCORE'; payload: string }
  | { type: 'ADD_NOT_TAG'; payload: string }
  | { type: 'REMOVE_NOT_TAG'; payload: string }
  | { type: 'ADD_NOT_OFFICE'; payload: string }
  | { type: 'REMOVE_NOT_OFFICE'; payload: string }
  | { type: 'ADD_NOT_USER'; payload: string }
  | { type: 'REMOVE_NOT_USER'; payload: string }
  | {
      type: 'ENABLE_FLYNCK_OPTIONS';
      payload: {
        enable: boolean;
        initialState: UpdateSettingsState['flynck'];
      };
    }
  | { type: 'SET_FLYNCK_IMPORT_SETUP_ID'; payload: string }
  | { type: 'SET_FLYNCK_CONTEXT_ID'; payload: string }
  | {
      type: 'RESET';
      payload: UpdateSettingsState;
    }
  | { type: 'SET_CALLBACK_PHONE_NUMBER'; payload: string };

export type UpdateSettingsState = {
  description: string | null;
  services: Array<LfaServiceDetailsFieldsFragment>;
  rules: {
    minLeadscore: string;
    notOfficeId: Array<string>;
    notTags: Array<string>;
    notUserId: Array<string>;
  };
  flynck: {
    importSetupID: string;
    contextID: string;
  } | null;
  callbackPhoneNumber: string | null;
};

const settingsReducer = (
  state: UpdateSettingsState,
  action: UpdateSettingsAction,
): UpdateSettingsState => {
  switch (action.type) {
    case 'SET_DESCRIPTION':
      return {
        ...state,
        description: action.payload,
      };
    case 'TOGGLE_SERVICE':
      return {
        ...state,
        services: state.services.some(
          service => service.id === action.payload.service.id,
        )
          ? state.services.filter(
              service => service.id !== action.payload.service.id,
            )
          : [...state.services, action.payload.service],
      };

    case 'TOGGLE_LOCATIONS':
      return {
        ...state,
        services: state.services.map(service => {
          if (service.id === action.payload.serviceId) {
            const currentLocations = service.locations || [];
            const locationToToggle = action.payload.location;

            const locations = currentLocations.map(location => {
              if (location.type === locationToToggle) {
                return { ...location, enabled: !location.enabled };
              }
              return location;
            });

            return {
              ...service,
              locations,
            };
          }
          return service;
        }),
      };
    case 'SET_LOCATION_NOTE_AND_WEIGHT':
      return {
        ...state,
        services: state.services.map(service => {
          if (service.id === action.payload.serviceId) {
            const currentLocations = service.locations || [];
            const locations = currentLocations.map(location => {
              if (location.type === action.payload.locationType) {
                return {
                  ...location,
                  note: action.payload.note,
                  weight: action.payload.weight,
                };
              }
              return location;
            });
            return { ...service, locations };
          }
          return service;
        }),
      };

    case 'SET_MIN_LEADSCORE':
      return {
        ...state,
        rules: {
          ...state.rules,
          minLeadscore: action.payload,
        },
      };
    case 'ADD_NOT_TAG':
      return {
        ...state,
        rules: {
          ...state.rules,
          notTags: [...state.rules.notTags, action.payload],
        },
      };
    case 'REMOVE_NOT_TAG':
      return {
        ...state,
        rules: {
          ...state.rules,
          notTags: state.rules.notTags.filter(tag => tag !== action.payload),
        },
      };
    case 'ADD_NOT_OFFICE':
      return {
        ...state,
        rules: {
          ...state.rules,
          notOfficeId: [...state.rules.notOfficeId, action.payload],
        },
      };
    case 'REMOVE_NOT_OFFICE':
      return {
        ...state,
        rules: {
          ...state.rules,
          notOfficeId: state.rules.notOfficeId.filter(
            id => id !== action.payload,
          ),
        },
      };
    case 'ADD_NOT_USER':
      return {
        ...state,
        rules: {
          ...state.rules,
          notUserId: [...state.rules.notUserId, action.payload],
        },
      };
    case 'REMOVE_NOT_USER':
      return {
        ...state,
        rules: {
          ...state.rules,
          notUserId: state.rules.notUserId.filter(id => id !== action.payload),
        },
      };
    case 'SET_FLYNCK_IMPORT_SETUP_ID':
      return {
        ...state,
        flynck: {
          contextID: state.flynck?.contextID || '',
          importSetupID: action.payload,
        },
      };
    case 'SET_FLYNCK_CONTEXT_ID':
      return {
        ...state,
        flynck: {
          importSetupID: state.flynck?.importSetupID || '',
          contextID: action.payload,
        },
      };
    case 'SET_CALLBACK_PHONE_NUMBER':
      return {
        ...state,
        callbackPhoneNumber: action.payload,
      };

    case 'RESET':
      return action.payload;
    default:
      return state;
  }
};

export default settingsReducer;
